import Card from "./Card";
import classes from "./AssetCard.module.css"

import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faStar } from "@fortawesome/free-solid-svg-icons";

const formatPrice = (value) =>
  parseFloat(value).toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const formatVariation = (value) =>
  `${value > 0 ? '+' : ''}${value.toFixed(2)}%`;

function calcularPLJusto({ crescimento, roe }) {
  if (!crescimento) return null;

  // Base: PEG = 1 → PL ≈ crescimento (%)
  let plBase = crescimento * 100;

  // Ajuste de qualidade via ROE
  if (roe) {
    if (roe < 0.08) plBase *= 0.7;       // baixa qualidade
    else if (roe > 0.15) plBase *= 1.2;  // alta qualidade
  }

  return plBase;
}

function precoJustoPL(dados) {
  const plJusto = calcularPLJusto(dados);
  if (!plJusto || !dados.lpa) return null;

  return dados.lpa * plJusto;
}

function precoJustoPVP(dados) {
  if (!dados.vpa || !dados.roe) return null;

  let pvpJusto = 1;

  if (dados.roe < 0.08) pvpJusto = 0.8;
  else if (dados.roe > 0.15) pvpJusto = 1.5;

  return dados.vpa * pvpJusto;
}

function precoPVP(dados) {
  return dados.currentPrice / dados.pvp
}

function precoJustoDY(dados) {
  if (!dados.dy || !dados.currentPrice) return null;
  if (dados.dy >= 0 || dados.currentPrice >= 0) return null;

  const dividendo = dados.currentPrice * dados.dy;

  // taxa derivada do crescimento e risco
  let retornoDesejado = 0.10;

  if (dados.rG > 0.20) retornoDesejado = 0.08;
  if (dados.roe < 0.08) retornoDesejado = 0.12;

  return dividendo / retornoDesejado;
}

function precoJustoGraham(dados) {
  if (!dados.lpa || !dados.vpa) return null;
  if (dados.lpa >= 0 || dados.vpa >= 0) return null;

  return Math.sqrt(22.5 * dados.lpa * dados.vpa);
}

function precoJustoEVEBITDA(dados) {
  if (!dados.evEbitda || !dados.currentPrice) return null;

  let evJusto = 6;

  if (dados.rG > 0.20) evJusto = 7;
  if (dados.roe < 0.08) evJusto = 5;

  return dados.currentPrice * (dados.evEbitda / evJusto);
}

function precoJustoPEGAvancado({ lpa, rG, roe }) {
  if (!lpa || !rG) return null;

  let g = rG < 1 ? rG * 100 : rG;

  // ajuste de qualidade
  if (roe) {
    if (roe < 0.08) g *= 0.7;
    else if (roe > 0.15) g *= 1.2;
  }

  return lpa * g;
}

function precoJustoFinal(dados) {
  const resultados = {
    pl: precoJustoPL(dados),
    pvpJusto: precoJustoPVP(dados),
    pvp: precoPVP(dados),
    dy: precoJustoDY(dados),
    graham: precoJustoGraham(dados),
    evEbitda: precoJustoEVEBITDA(dados),
    peg: precoJustoPEGAvancado(dados)
  };

  const valores = Object.values(resultados).filter(v => { return v !== null && verificarEscala(v, dados.currentPrice) });

  const media = valores.reduce((a, b) => a + b, 0) / valores.length;
  if (isNaN(media) || media < 0) return ''
  return media
}

function verificarEscala(precoJusto, precoAtual, tolerancia = 0.7) {
  // Evita erro com valores zero ou negativos (log de <= 0 não existe)
  if (precoJusto <= 0 || precoAtual <= 0) return false;

  const logJusto = Math.log10(precoJusto);
  const logAtual = Math.log10(precoAtual);

  const diferencaEscala = Math.abs(logJusto - logAtual);

  // Se a diferença for > tolerancia, os números estão em escalas diferentes
  return diferencaEscala <= tolerancia;
}

const AssetList = ({ title, icon, data = [], type, dolar = false }) => {
  const isGain = type === 'gain';

  return (
    <Card>
      <div className={classes.cardHeader}>
        <h5 className={`${classes.cardTitle} p-1 `}>
          {icon} {title}
        </h5>
      </div>

      <div>
        {data.map((asset) => {
          const variation = parseFloat(asset.vD);
          const justo = precoJustoFinal(asset)

          let currentPrice = (dolar ? '$' : 'R$')+ formatPrice(asset.currentPrice)          
          let precoJusto = '-'
          if (justo !== '') {
            precoJusto = (dolar ? '$' : 'R$') + formatPrice(justo)
          }


          return (
            <div key={asset.id} className={`${classes.assetItem} row`}>
              <div className={`${classes.tickerInfo} col-6`}>
                <span className={classes.tickerName}><strong>{asset.title}</strong></span>
                <span className={classes.price}>
                  <small>ATUAL</small>
                </span>
                <span className={classes.price}>
                  <small>JUSTO</small>
                </span>
              </div>

              <div className={`${classes.assetValues} col-6`}>
                <span
                  className={`variationBadge ${isGain ? classes.gain : classes.loss}`}
                >
                  {formatVariation(variation)}
                </span>
                <span className={classes.price}>
                  <small>{currentPrice}</small>
                </span>
                <span className={classes.price}>
                  <small>{precoJusto}</small>
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
};

const AssetCard = ({ title = '', altas = [], baixas = [], dolar = false }) => {
  return (
    <Card>
      <Row>
        <Col>
          <h4 className={classes.title}><FontAwesomeIcon icon={faStar} />&nbsp;{title}</h4>
        </Col>
      </Row>
      <Row className="g-0">
        <div className="col g-0">
          <AssetList
            title={<>Maiores<br />Altas</>}
            icon="🚀"
            data={altas}
            type="gain"
            dolar={dolar}
          />
        </div>
        <div className="col g-0">
          <AssetList
            title={<>Maiores<br />Baixas</>}
            icon="📉"
            data={baixas}
            type="loss"
            dolar={dolar}
          />
        </div>
      </Row>
    </Card>
  );
};

export default AssetCard;